import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '@shared/ui/badge';
import { StepHelpers } from '@shared/components/Stepper';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { Button } from '@shared/ui/button';
import { useLocation } from 'react-router-dom';
import { useAllAccounts } from "../hook";
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';



export const SelectAccountsStep = ({
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious
}: {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const preSelectedIds = location.state?.accountIds || [];

  const [page, setPage] = useState(1);
  const limit = 50;

  const columns: TableColumn<any>[] = useMemo(() => [
    {
      key: "account",
      header: t("Account"),
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium bg-blue-100 text-blue-700`}>
            {row.initials}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{row.name}</div>
            <div className="text-gray-500 text-xs">{row.customerName}</div>
          </div>
        </div>
      )
    },
    { key: "consumerNo", header: t("Consumer No.") },
    {
      key: "type",
      header: t("Type"),
      render: (_, row) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${row.typeColor}`}>
          {row.type}
        </span>
      )
    },
    {
      key: "address",
      header: t("Service Address"),
      width: "w-1/4",
      render: (_, row) => (
        <div className="truncate max-w-[200px]" title={row.address}>{row.address}</div>
      )
    },
    {
      key: "services",
      header: t("Services"),
      render: (_, row) => (
        <div className="flex flex-wrap gap-1.5">
          {row.services.map((s: string) => (
            <span key={s} className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{s}</span>
          ))}
        </div>
      )
    },
    {
      key: "status",
      header: t("Status"),
      render: (_, row) => (
        <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
          {row.status}
        </div>
      )
    }
  ], [t]);

  const { consumerId } = getLoginDataFromStorage() || {};
  const remoteUtilityId = getRemoteUtilityId();

  const { data: allAccounts, isLoading } = useAllAccounts({
    page: page,
    limit: limit,
    active_consumers: 1,
    remote_utility_id: remoteUtilityId,
    is_sub_account: 1,
    consumer_id: getLoginDataFromStorage().consumerId,
  });

  const tableData = useMemo(() => {
    if (!allAccounts?.results) return [];
    
    // Map the API data to match the UI requirements
    let mapped = allAccounts.results.map((item: any) => ({
      id: item.id.toString(),
      initials: `${item.firstName?.charAt(0) || ''}${item.lastName?.charAt(0) || ''}`.toUpperCase(),
      name: `${item.firstName} ${item.lastName}`,
      customerName: item.ownershipDisplay,
      consumerNo: item.consumerNo,
      type: item.ownershipDisplay,
      typeColor: 'bg-blue-100 text-blue-700',
      address: `${item.territoryData?.service?.address || ''} ${item.territoryData?.service?.unit || ''}`.trim(),
      services: item.additionalData?.nonMeterServices || [],
      status: item.statusDisplay
    }));

    // Strictly filter to show ONLY those accounts passed via state from AllAccountsTab
    if (preSelectedIds.length > 0) {
      mapped = mapped.filter((account: any) => preSelectedIds.includes(account.id));
    }

    return mapped;
  }, [allAccounts, preSelectedIds]);

  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    return stepHelpers?.getStepData(currentStepIndex)?.selectedIds || preSelectedIds;
  });

  useEffect(() => {
    if (stepHelpers && !stepHelpers.getStepData(currentStepIndex)) {
      const selectedAccs = tableData.filter((t: any) => selectedIds.includes(t.id));
      stepHelpers.setStepData(currentStepIndex, { selectedIds, selectedAccounts: selectedAccs });
    }
  }, [stepHelpers, currentStepIndex, selectedIds, tableData]);

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedIds(newSelectedIds);
    const selectedAccs = tableData.filter((t: any) => newSelectedIds.includes(t.id));
    stepHelpers?.setStepData(currentStepIndex, { selectedIds: newSelectedIds, selectedAccounts: selectedAccs });
  };
  
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAccounts = tableData.filter((account: any) => 
    account.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.consumerNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{t("Select Accounts")}</h3>
        <p className="text-sm text-gray-500">
          {t("Choose which sub-accounts this request applies to · ")}<span className="font-medium text-blue-600">{t("{{selected}} of {{total}} selected", { selected: selectedIds.length, total: tableData.length })}</span>
        </p>
      </div>

      <GenericTable
        data={filteredAccounts}
        loading={isLoading}
        columns={columns}
        rowKey="id"
        selectable={true}
        selectedRows={selectedIds}
        onSelectionChange={handleSelectionChange}
        onSearch={(term) => setSearchQuery(term)}
        searchPlaceholder={t("Search accounts...")}
        emptyMessage={t("No accounts found.")}
      />

      <div className="flex justify-between pt-6 border-t border-gray-200">
        <Button
          onClick={onPrevious}
          disabled={currentStepIndex === 0}
          variant="outline"
        >
          {t("Previous")}
        </Button>
        <Button
          onClick={onNext}
          disabled={selectedIds.length === 0}
        >
          {t("Continue")}
        </Button>
      </div>
    </div>
  );
};
